/**
 * Tooltips and the definition affordance.
 *
 * WHY THESE ARE PORTALLED
 * ----------------------
 * Every metric tile, chart and table cell that needs one of these sits inside a
 * card, and a card clips: `overflow-hidden` for the rounded corner, `overflow-x`
 * for a wide table, `overflow-y` for a scrolling panel. An absolutely positioned
 * bubble inside that subtree gets cut in half or, worse, silently widens the
 * card's scroll area. Both bubbles therefore render into `document.body` with
 * `position: fixed`, measured against the live viewport, and flip to the
 * opposite side when the preferred one would run off screen.
 *
 * WHY THE FIGURE MATTERS
 * ----------------------
 * `InfoTip` is the only place the user can read what a metric actually counts
 * (`METRICS[key].definition`). A number nobody can define is a number nobody
 * trusts, so this control is a real `<button>`: reachable by keyboard, exposed
 * to a screen reader as a described disclosure, and openable by touch — not a
 * hover-only affordance that a phone can never reveal.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const GAP = 8;   // anchor -> bubble
const EDGE = 8;  // bubble -> viewport edge
const ARROW_INSET = 12; // keeps the arrow off the bubble's rounded corners

const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const clamp = (v, min, max) => (max < min ? min : Math.min(Math.max(v, min), max));

/**
 * Resolve a fixed-position placement for `bubble` against `anchor`.
 *
 * Flipping is decided on the axis the placement runs along; the cross axis is
 * clamped into the viewport instead, which is why the arrow offset is returned
 * separately — after a clamp the bubble's centre no longer sits over the anchor,
 * and an arrow drawn at the centre would point at the wrong thing.
 *
 * @returns {{ side: string, top: number, left: number, arrow: number }}
 */
function computePosition(anchor, bubble, preferred) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const room = {
    top: anchor.top - GAP - EDGE,
    bottom: vh - EDGE - anchor.bottom - GAP,
    left: anchor.left - GAP - EDGE,
    right: vw - EDGE - anchor.right - GAP,
  };
  const needed = (s) => (s === 'top' || s === 'bottom' ? bubble.height : bubble.width);

  let side = OPPOSITE[preferred] ? preferred : 'top';
  if (room[side] < needed(side)) {
    const alt = OPPOSITE[side];
    // Take the flip when it fits, or when it is simply the roomier half — a
    // bubble taller than both halves should at least open into the larger one.
    if (room[alt] >= needed(alt) || room[alt] > room[side]) side = alt;
  }

  const centreX = anchor.left + anchor.width / 2;
  const centreY = anchor.top + anchor.height / 2;

  let top;
  let left;
  if (side === 'top' || side === 'bottom') {
    left = clamp(centreX - bubble.width / 2, EDGE, vw - EDGE - bubble.width);
    top = side === 'top' ? anchor.top - GAP - bubble.height : anchor.bottom + GAP;
    top = clamp(top, EDGE, Math.max(EDGE, vh - EDGE - bubble.height));
  } else {
    top = clamp(centreY - bubble.height / 2, EDGE, vh - EDGE - bubble.height);
    left = side === 'left' ? anchor.left - GAP - bubble.width : anchor.right + GAP;
    left = clamp(left, EDGE, Math.max(EDGE, vw - EDGE - bubble.width));
  }

  const arrow =
    side === 'top' || side === 'bottom'
      ? clamp(centreX - left, ARROW_INSET, Math.max(ARROW_INSET, bubble.width - ARROW_INSET))
      : clamp(centreY - top, ARROW_INSET, Math.max(ARROW_INSET, bubble.height - ARROW_INSET));

  return { side, top, left, arrow };
}

/**
 * Measure-then-place, re-run for as long as the bubble is open.
 *
 * Scroll is listened to in the capture phase so a bubble anchored inside a
 * scrolling table follows its anchor: scroll events on an inner element do not
 * bubble to `window`.
 */
function useFloating(open, anchorRef, placement, content) {
  const bubbleRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    setPos(computePosition(anchor.getBoundingClientRect(), bubble.getBoundingClientRect(), placement));
  }, [anchorRef, placement]);

  useLayoutEffect(() => {
    if (!open) {
      // Drop the stale position so the next open measures before it paints
      // instead of flashing at the previous anchor.
      setPos(null);
      return undefined;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // `content` is a dependency because a longer string is a taller bubble.
  }, [open, place, content]);

  return { bubbleRef, pos };
}

/** Escape closes the topmost bubble only, and never reaches the modal behind it. */
function useEscape(open, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      // A tip opened inside a Modal must not dismiss the Modal as well.
      event.stopPropagation();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);
}

const ARROW_SIDE = {
  top: 'border-b border-r',
  bottom: 'border-t border-l',
  left: 'border-t border-r',
  right: 'border-b border-l',
};

const arrowStyle = (side, offset) => {
  switch (side) {
    case 'bottom': return { left: offset, top: -4, marginLeft: -4 };
    case 'left': return { top: offset, right: -4, marginTop: -4 };
    case 'right': return { top: offset, left: -4, marginTop: -4 };
    default: return { left: offset, bottom: -4, marginLeft: -4 };
  }
};

/**
 * The floating surface itself. Shared so the hover tooltip and the info bubble
 * cannot drift apart visually.
 *
 * z-index sits above the legacy `.floating-nav` (1000) and any dialog, because a
 * definition opened from inside a Drawer has to be readable there too.
 */
function Bubble({ id, pos, bubbleRef, interactive, className, children, ...rest }) {
  const side = pos ? pos.side : 'top';
  return createPortal(
    <div
      {...rest}
      ref={bubbleRef}
      id={id}
      role="tooltip"
      style={{
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        // Hidden, not unmounted, for the first paint: the bubble has to be in
        // the document at its natural size before it can be positioned.
        visibility: pos ? 'visible' : 'hidden',
      }}
      className={cx(
        'fixed z-[1200] rounded-md border border-line bg-surface px-2.5 py-1.5 shadow-pop',
        'animate-fade-in',
        interactive ? 'pointer-events-auto' : 'pointer-events-none',
        className,
      )}
    >
      {children}
      <span
        aria-hidden="true"
        style={arrowStyle(side, pos ? pos.arrow : 0)}
        className={cx('absolute h-2 w-2 rotate-45 bg-surface border-line', ARROW_SIDE[side])}
      />
    </div>,
    document.body,
  );
}

/**
 * The 16px circled question mark that carries a metric's definition.
 *
 * Opens on hover, on focus, and on click. Click *pins* it: hovering away or
 * tabbing on leaves a pinned bubble up, so the text can be read and selected on
 * a touch screen, and Escape or a click elsewhere puts it away.
 *
 * @param {object} props
 * @param {*}      props.content  string or node — the definition
 * @param {string} [props.label]  accessible name; make it metric-specific when
 *                                several tips sit in one view
 */
export function InfoTip({ content, label = 'What this means', className = '', ...rest }) {
  const id = useId();
  const anchorRef = useRef(null);
  const pinnedRef = useRef(false);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    pinnedRef.current = false;
    setOpen(false);
  }, []);

  const { bubbleRef, pos } = useFloating(open, anchorRef, 'top', content);
  useEscape(open, close);

  // A pinned bubble outlives blur, so the only thing left to dismiss it is a
  // press somewhere else. Pointerdown rather than click: a press that starts
  // outside should not need to complete on the same element.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      const inAnchor = anchorRef.current && anchorRef.current.contains(event.target);
      const inBubble = bubbleRef.current && bubbleRef.current.contains(event.target);
      if (!inAnchor && !inBubble) close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, close, bubbleRef]);

  if (content === null || content === undefined || content === '') return null;

  const toggle = () => {
    if (pinnedRef.current) {
      close();
      return;
    }
    pinnedRef.current = true;
    setOpen(true);
  };

  return (
    <>
      <button
        {...rest}
        type="button"
        ref={anchorRef}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={toggle}
        onPointerEnter={() => setOpen(true)}
        onPointerLeave={() => { if (!pinnedRef.current) setOpen(false); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!pinnedRef.current) setOpen(false); }}
        className={cx(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-[-0.125em]',
          'text-ink-500 transition-colors duration-fast hover:text-ink-700',
          open && 'text-ink-700',
          className,
        )}
      >
        <i className="fas fa-circle-question text-micro leading-none" aria-hidden="true" />
      </button>

      {open && (
        <Bubble id={id} pos={pos} bubbleRef={bubbleRef} interactive className="w-max max-w-[264px] py-2">
          <span className="block text-label leading-snug text-ink-700">{content}</span>
        </Bubble>
      )}
    </>
  );
}

/**
 * Generic hover/focus tooltip — chart marks, truncated cells, icon affordances.
 *
 * Deliberately narrower than `InfoTip`: no click handling, and the bubble itself
 * is `pointer-events: none`, so wrapping a clickable mark never intercepts the
 * click that drills through. A short open delay stops a row of marks from
 * strobing as the pointer crosses them.
 *
 * The child is cloned with `aria-describedby` when it is a single element, so
 * the tooltip text is announced against the thing it describes rather than
 * floating unattached in the body.
 *
 * @param {object} props
 * @param {*} props.content                      string or node; falsy renders children bare
 * @param {'top'|'bottom'|'left'|'right'} [props.placement]
 * @param {string} [props.className]             on the anchor wrapper — pass
 *                                               `block w-full min-w-0` inside a
 *                                               truncating table cell
 */
export function Tooltip({ content, children, placement = 'top', className = '' }) {
  const id = useId();
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const hide = useCallback(() => {
    cancel();
    setOpen(false);
  }, [cancel]);
  const show = () => {
    cancel();
    timerRef.current = setTimeout(() => setOpen(true), 90);
  };

  useEffect(() => cancel, [cancel]);

  const { bubbleRef, pos } = useFloating(open, anchorRef, placement, content);
  useEscape(open, hide);

  if (content === null || content === undefined || content === '') return children ?? null;

  // React's onFocus/onBlur are focusin/focusout, so they reach the wrapper from
  // whatever inside it took focus — no cloning needed for the handlers.
  const isElement = React.isValidElement(children);
  const describedBy = cx(isElement ? children.props['aria-describedby'] : null, open ? id : null) || undefined;
  const described = isElement
    ? React.cloneElement(children, { 'aria-describedby': describedBy })
    : children;

  return (
    <>
      <span
        ref={anchorRef}
        // A plain-text child has nothing to hang the description on, so the
        // wrapper carries it instead.
        aria-describedby={isElement ? undefined : describedBy}
        className={cx('inline-flex min-w-0 max-w-full', className)}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {described}
      </span>

      {open && (
        <Bubble id={id} pos={pos} bubbleRef={bubbleRef}>
          <span className="block max-w-[264px] whitespace-normal break-words text-micro leading-snug text-ink-700">
            {content}
          </span>
        </Bubble>
      )}
    </>
  );
}
